import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Zap, ArrowRight, Check } from 'lucide-react';
import api from '../api';

export default function Login() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const res = await api.post('/auth/login', form);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      navigate('/');
    } catch (err) { setError(err.response?.data?.detail || 'Invalid credentials'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @media (min-width: 768px) {
          .login-container { flex-direction: row !important; }
          .login-left { max-width: 480px !important; border-right: 1px solid var(--border) !important; }
          .login-right { display: flex !important; }
          .login-right-panel { display: flex !important; }
        }
        @media (max-width: 767px) {
          .login-left { padding: 32px 24px !important; width: 100% !important; max-width: 100% !important; }
          .login-right { display: none !important; }
          .login-brand { margin-bottom: 32px !important; }
          .login-heading { font-size: 36px !important; }
        }
      `}</style>

      <div className="login-container" style={{ display: 'flex', flex: 1 }}>
        {/* Left — form */}
        <div className="login-left" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '52px 48px', maxWidth: 480 }}>
          <div style={{ width: '100%', maxWidth: 380 }} className="fade-in">
            <div className="login-brand" style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 44 }}>
              <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #6366f1, #a855f7)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 24px rgba(99,102,241,0.5)', flexShrink: 0 }}>
                <Zap size={19} color="white" fill="white" />
              </div>
              <div style={{ fontFamily: 'Bebas Neue', fontSize: 20, letterSpacing: '0.05em' }}>ASTRAAUTOMATIONS</div>
            </div>

            <div style={{ marginBottom: 32 }}>
              <h1 className="login-heading" style={{ fontSize: 42, marginBottom: 8 }}>WELCOME BACK</h1>
              <p style={{ color: 'var(--text2)', fontSize: 15 }}>Sign in to continue building automations</p>
            </div>

            <form onSubmit={submit}>
              <div style={{ marginBottom: 16 }}>
                <label>Email Address</label>
                <input type="email" placeholder="you@company.com" value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })} required autoFocus />
              </div>
              <div style={{ marginBottom: 28 }}>
                <label>Password</label>
                <input type="password" placeholder="••••••••" value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })} required />
              </div>
              {error && (
                <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 20, padding: '12px 16px', background: 'var(--red-bg)', borderRadius: 9, border: '1px solid rgba(239,68,68,0.25)', fontWeight: 500 }}>
                  {error}
                </div>
              )}
              <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
                {loading ? 'Signing in...' : <><span>Sign In</span><ArrowRight size={17} /></>}
              </button>
            </form>

            <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: 'var(--text2)' }}>
              No account?{' '}
              <Link to="/register" style={{ color: 'var(--accent3)', textDecoration: 'none', fontWeight: 700 }}>Sign up free →</Link>
            </p>

            {/* Mobile-only feature list */}
            <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--border)', display: 'block' }} className="mobile-features">
              <style>{`.mobile-features { display: block; } @media(min-width:768px){.mobile-features{display:none !important;}}`}</style>
              {['AI builds real working automations in seconds', 'Works with Gmail, Sheets, Telegram & more', 'No code. No technical knowledge needed.'].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 20, height: 20, borderRadius: 6, background: 'var(--green-bg)', border: '1px solid var(--green-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    <Check size={11} color="var(--green)" strokeWidth={3} />
                  </div>
                  <span style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right panel — desktop only */}
        <div className="login-right" style={{ display: 'none', flex: 1, background: 'var(--bg2)', alignItems: 'center', justifyContent: 'center', padding: 64, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.15), transparent 70%)', top: '15%', left: '25%', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(168,85,247,0.12), transparent 70%)', bottom: '15%', right: '15%', pointerEvents: 'none' }} />
          <div style={{ maxWidth: 380, position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--accent-glow)', border: '1px solid rgba(99,102,241,0.35)', borderRadius: 20, padding: '5px 14px', marginBottom: 24 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--green)', animation: 'shimmer 2s ease infinite' }} />
              <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>AI-Powered Automation</span>
            </div>
            <div className="display" style={{ fontFamily: 'Bebas Neue', fontSize: 52, lineHeight: 0.95, marginBottom: 20, color: 'var(--text)' }}>
              AUTOMATE<br />ANYTHING.<br /><span style={{ background: 'linear-gradient(135deg, var(--accent3), var(--purple))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>NO CODE.</span>
            </div>
            <p style={{ fontSize: 15, color: 'var(--text2)', lineHeight: 1.8, marginBottom: 36 }}>
              Describe it in plain English. AI builds and deploys it. Your business runs itself.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {['Works with Gmail, Sheets, Telegram & more', 'AI builds real working automations in seconds', 'No code. No technical knowledge needed.'].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--green-bg)', border: '1px solid var(--green-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Check size={12} color="var(--green)" strokeWidth={3} />
                  </div>
                  <span style={{ fontSize: 14, color: 'var(--text2)', fontWeight: 500 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
